/**
 * Einrichten — der Katalog, der eine Einstieg (Einrichten-Neubau P2).
 *
 * Objektarten als Kacheln mit Bestand, Inaktiv-Zähler und letzter Änderung,
 * darüber die Namenssuche über alles. Neue Arten führen ins Objektblatt,
 * die noch alten (Kunden, Benutzer, Stationen, Strecken) auf ihre
 * bestehenden Seiten — bis P4–P6 sie nachziehen.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../App';
import { LeitstandRahmen } from '../../components/leitstand/LeitstandRahmen';
import { Notiz } from '../../components/leitstand';
import { ARTEN as WEB_ARTEN } from './arten';

interface Kachel {
  art: string;
  label: string;
  bestand: number;
  inaktiv: number;
  zuletztGeaendert: string | null;
}

interface Treffer {
  art: string;
  label: string;
  id: string;
  name: string;
  isActive: boolean;
}

function datumKurz(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('de-DE', {
    timeZone: 'Europe/Berlin',
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
  });
}

export default function EinrichtenKatalog() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [kacheln, setKacheln] = useState<Kachel[]>([]);
  const [laden, setLaden] = useState(true);
  const [fehler, setFehler] = useState('');

  const [suchtext, setSuchtext] = useState('');
  const [treffer, setTreffer] = useState<Treffer[] | null>(null);
  const sucheLaeuft = useRef(0);

  const lade = useCallback(async () => {
    setLaden(true);
    setFehler('');
    try {
      const res = await fetch('/api/einrichten/katalog', { credentials: 'include' });
      if (!res.ok) throw new Error('Der Katalog konnte nicht geladen werden');
      const body = await res.json();
      setKacheln(body.kacheln ?? []);
    } catch (e) {
      setFehler(e instanceof Error ? e.message : 'Der Katalog konnte nicht geladen werden');
    } finally {
      setLaden(false);
    }
  }, []);

  useEffect(() => {
    void lade();
  }, [lade]);

  // Suche entprellt; ein Zähler verwirft veraltete Antworten
  useEffect(() => {
    const begriff = suchtext.trim();
    if (begriff.length < 2) {
      setTreffer(null);
      return;
    }
    const lauf = ++sucheLaeuft.current;
    const t = setTimeout(async () => {
      try {
        const res = await fetch(`/api/einrichten/suche?q=${encodeURIComponent(begriff)}`, {
          credentials: 'include',
        });
        if (!res.ok) return;
        const body = await res.json();
        if (sucheLaeuft.current === lauf) setTreffer(body.treffer ?? []);
      } catch {
        /* Suche ist Komfort — Fehler bleiben still */
      }
    }, 300);
    return () => clearTimeout(t);
  }, [suchtext]);

  const zielFuer = (t: Treffer): string => `/einrichten/${t.art}/${t.id}`;

  return (
    <LeitstandRahmen titel="Katalog" benutzer={user?.displayName}>
      <div className="flex flex-col gap-6">
        <input
          type="text"
          value={suchtext}
          onChange={(e) => setSuchtext(e.target.value)}
          placeholder="Name tippen — Kunde, Maschine, Benutzer, Programm …"
          className="w-full rounded-leitstand border border-linie-2 bg-flaeche px-4 py-3 text-[15px] text-tinte placeholder:text-tinte-3 focus:border-akzent focus:outline-none"
        />

        {fehler && (
          <Notiz titel="Konnte nicht laden">
            {fehler}{' '}
            <button onClick={() => void lade()} className="underline">
              Erneut versuchen
            </button>
          </Notiz>
        )}

        {treffer !== null ? (
          <div className="rounded-leitstand border border-linie bg-flaeche">
            {treffer.length === 0 ? (
              <p className="px-4 py-6 text-center text-sm text-tinte-3">Nichts gefunden.</p>
            ) : (
              treffer.map((t, i) => (
                <button
                  key={`${t.art}-${t.id}`}
                  onClick={() => navigate(zielFuer(t))}
                  className={`flex w-full items-center gap-3 px-4 py-3 text-left hover:bg-flaeche-2 ${
                    i < treffer.length - 1 ? 'border-b border-linie' : ''
                  }`}
                >
                  <span className="font-zahl text-[10px] uppercase tracking-[0.13em] text-tinte-3">
                    {t.label}
                  </span>
                  <span className={`text-sm ${t.isActive ? '' : 'text-tinte-3 line-through'}`}>
                    {t.name}
                  </span>
                  {!t.isActive && (
                    <span className="font-zahl text-[11px] text-tinte-3">außer Betrieb</span>
                  )}
                  <span className="ml-auto font-zahl text-tinte-3">→</span>
                </button>
              ))
            )}
          </div>
        ) : (
          !laden &&
          !fehler && (
            <div className="grid gap-3 [grid-template-columns:repeat(auto-fill,minmax(240px,1fr))]">
              {kacheln.map((k) => {
                const inhalt = (
                  <>
                    <div className="flex items-baseline justify-between gap-2">
                      <span className="text-[14.5px] font-semibold">{k.label}</span>
                      <span className="font-zahl text-[21px] font-semibold">
                        {k.bestand.toLocaleString('de-DE')}
                      </span>
                    </div>
                    <div className="mt-2 flex flex-wrap justify-between gap-2 text-xs text-tinte-3">
                      <span>{k.inaktiv > 0 ? `${k.inaktiv} außer Betrieb` : 'alle aktiv'}</span>
                      <span className="font-zahl">geändert {datumKurz(k.zuletztGeaendert)}</span>
                    </div>
                  </>
                );
                const klasse =
                  'rounded-leitstand border border-linie bg-flaeche px-4 py-[15px] text-left transition-colors hover:border-linie-2';
                return (
                  <Link key={k.art} to={`/einrichten/${k.art}`} className={klasse}>
                    {inhalt}
                  </Link>
                );
              })}
              <Link
                to="/einrichten/geraet-koppeln"
                className="rounded-leitstand border border-linie bg-flaeche px-4 py-[15px] text-left transition-colors hover:border-linie-2"
              >
                <div className="flex items-baseline justify-between gap-2">
                  <span className="text-[14.5px] font-semibold">Gerät koppeln</span>
                </div>
                <div className="mt-2 flex flex-wrap justify-between gap-2 text-xs text-tinte-3">
                  <span>neues Terminal per QR-Scan einrichten</span>
                </div>
              </Link>
              <Link
                to="/einrichten/schichten"
                className="rounded-leitstand border border-linie bg-flaeche px-4 py-[15px] text-left transition-colors hover:border-linie-2"
              >
                <div className="flex items-baseline justify-between gap-2">
                  <span className="text-[14.5px] font-semibold">Schichten</span>
                </div>
                <div className="mt-2 flex flex-wrap justify-between gap-2 text-xs text-tinte-3">
                  <span>Schichtarten und Regeln — der Kalender im Betrieb zeigt das Ergebnis</span>
                </div>
              </Link>
              <Link
                to="/einrichten/geraete"
                className="rounded-leitstand border border-linie bg-flaeche px-4 py-[15px] text-left transition-colors hover:border-linie-2"
              >
                <div className="flex items-baseline justify-between gap-2">
                  <span className="text-[14.5px] font-semibold">Geräte</span>
                </div>
                <div className="mt-2 flex flex-wrap justify-between gap-2 text-xs text-tinte-3">
                  <span>holen die Daten der Taktwaschanlagen</span>
                </div>
              </Link>
            </div>
          )
        )}

        {laden && !fehler && kacheln.length === 0 && (
          <p className="py-12 text-center text-sm text-tinte-3">Lade Katalog …</p>
        )}

        {treffer === null && !laden && (
          <p className="text-[12.5px] text-tinte-3">
            {Object.keys(WEB_ARTEN).length < kacheln.length ? '' : ''}
          </p>
        )}
      </div>
    </LeitstandRahmen>
  );
}
